import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import axiosInstance from '../api';
import OffboardingModal from './OffboardingModal';
import MergeSuggestionsPanel from './MergeSuggestionsPanel';
import './PeoplePage.css';

const SOURCE_META = {
  AZURE_AD:  { label: 'Azure AD',  color: '#0078d4', icon: 'fa-microsoft' },
  GITHUB:    { label: 'GitHub',    color: '#24292e', icon: 'fa-github' },
  AWS_IAM:   { label: 'AWS IAM',   color: '#ff9900', icon: 'fa-aws' },
  GOOGLE:    { label: 'Google',    color: '#4285f4', icon: 'fa-google' },
  MANUAL:    { label: i18n.t('PeoplePage.sourceManual'),    color: '#6366f1', icon: 'fa-user-edit' },
};

function sourceChip(source) {
  const m = SOURCE_META[source] || { label: source, color: '#64748b', icon: 'fa-user' };
  return (
    <span
      className="pp-source-chip"
      style={{ background: m.color + '1a', color: m.color, border: `1px solid ${m.color}33` }}
    >
      <i className={`fab ${m.icon}`} style={{ marginRight: 4, fontSize: 11 }} />
      {m.label}
    </span>
  );
}

function statusBadge(isActive) {
  return isActive
    ? <span className="pp-badge pp-badge-active">{i18n.t('PeoplePage.statusActive')}</span>
    : <span className="pp-badge pp-badge-off">{i18n.t('PeoplePage.statusOffboarded')}</span>;
}

const TYPE_META = {
  HUMAN:   { label: i18n.t('PeoplePage.typeHuman'),   icon: 'fa-user',     cls: 'pp-type-human'   },
  SERVICE: { label: i18n.t('PeoplePage.typeService'), icon: 'fa-robot',    cls: 'pp-type-service' },
  ROLE:    { label: i18n.t('PeoplePage.typeRole'),    icon: 'fa-user-tag', cls: 'pp-type-role'    },
};

function typeBadge(accountType) {
  const m = TYPE_META[accountType];
  if (!m) return <span className="pp-type-badge pp-type-other">—</span>;
  return (
    <span className={`pp-type-badge ${m.cls}`}>
      <i className={`fas ${m.icon}`} /> {m.label}
    </span>
  );
}

function PeoplePage() {
  const { t } = useTranslation();
  const [people,       setPeople]       = useState([]);
  const [total,        setTotal]        = useState(0);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);
  const [search,       setSearch]       = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [deptFilter,   setDeptFilter]   = useState('');
  const [typeFilter,   setTypeFilter]   = useState('');   // '' | HUMAN | SERVICE | ROLE
  const [page,           setPage]           = useState(0);
  const [departments,    setDepartments]    = useState([]);
  const [offboarding,    setOffboarding]    = useState(null);  // person being offboarded
  const [suggestions,    setSuggestions]    = useState([]);    // pending duplicate suggestions
  const [busySuggestion, setBusySuggestion] = useState(null);  // suggestion id mid-request
  const [expanded,       setExpanded]       = useState({});    // personId -> linked-accounts expanded

  const PAGE_SIZE = 20;

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({
      search,
      source: sourceFilter,
      department: deptFilter,
      accountType: typeFilter,
      page,
      size: PAGE_SIZE,
    });
    axiosInstance.get(`/people?${params}`)
      .then(res => {
        const data = res.data;
        setPeople(Array.isArray(data.content) ? data.content : []);
        setTotal(data.total || 0);
        // collect unique departments for filter
        const depts = [...new Set(
          (data.content || [])
            .map(p => p.department)
            .filter(Boolean)
        )].sort();
        setDepartments(prev => {
          const merged = [...new Set([...prev, ...depts])].sort();
          return merged;
        });
      })
      .catch(() => setError(t('PeoplePage.errorLoad')))
      .finally(() => setLoading(false));
  }, [search, sourceFilter, deptFilter, typeFilter, page, t]);

  const loadSuggestions = useCallback(() => {
    axiosInstance.get('/people/suggestions?status=PENDING')
      .then(res => setSuggestions(Array.isArray(res.data.content) ? res.data.content : []))
      .catch(() => setSuggestions([]));  // non-fatal: panel just won't show
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadSuggestions(); }, [loadSuggestions]);

  const confirmSuggestion = (id) => {
    setBusySuggestion(id);
    axiosInstance.post(`/people/suggestions/${id}/confirm`)
      .then(() => { loadSuggestions(); load(); })
      .catch(() => setError(t('PeoplePage.mergeError', { defaultValue: 'Could not update the suggestion. You may need manager permissions.' })))
      .finally(() => setBusySuggestion(null));
  };

  const rejectSuggestion = (id) => {
    setBusySuggestion(id);
    axiosInstance.post(`/people/suggestions/${id}/reject`)
      .then(() => loadSuggestions())
      .catch(() => setError(t('PeoplePage.mergeError', { defaultValue: 'Could not update the suggestion. You may need manager permissions.' })))
      .finally(() => setBusySuggestion(null));
  };

  const unmergePerson = (personId) => {
    axiosInstance.post(`/people/persons/${personId}/unmerge`)
      .then(() => { loadSuggestions(); load(); })
      .catch(() => setError(t('PeoplePage.mergeError', { defaultValue: 'Could not update the suggestion. You may need manager permissions.' })));
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    return parts.length >= 2
      ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      : name.slice(0, 2).toUpperCase();
  };

  const avatarColor = (name) => {
    const colors = ['#1d4ed8','#059669','#b45309','#7c3aed','#dc2626','#0891b2'];
    let h = 0;
    for (let i = 0; i < (name || '').length; i++) h = (h * 31 + (name || '').charCodeAt(i)) % colors.length;
    return colors[h];
  };

  if (loading && people.length === 0) return (
    <div className="pp-page">
      <div className="pp-spinner-wrap"><div className="pp-spinner" /></div>
    </div>
  );

  if (error) return (
    <div className="pp-page">
      <div className="pp-error">
        <i className="fas fa-exclamation-circle" /> {error}
        <button className="pp-retry" onClick={load}>{t('PeoplePage.retry')}</button>
      </div>
    </div>
  );

  return (
    <div className="pp-page">
      {/* Header */}
      <div className="pp-header">
        <div className="pp-header-left">
          <h2 className="pp-title">
            <i className="fas fa-users" /> {t('PeoplePage.title')}
          </h2>
          <p className="pp-subtitle">
            {t('PeoplePage.subtitle')}
          </p>
        </div>
        <div className="pp-stat-pills">
          <span className="pp-stat-pill">
            <strong>{total}</strong> {t('PeoplePage.identitiesLabel')}
          </span>
          <span className="pp-stat-pill pp-stat-pill-green">
            <strong>{people.filter(p => p.isActive).length}</strong> {t('PeoplePage.activeOnThisPage')}
          </span>
        </div>
      </div>

      {/* Duplicate-person suggestions (T-0328 Part 3) */}
      <MergeSuggestionsPanel
        suggestions={suggestions}
        busyId={busySuggestion}
        onConfirm={confirmSuggestion}
        onReject={rejectSuggestion}
      />

      {/* Toolbar */}
      <div className="pp-toolbar">
        <div className="pp-search-wrap">
          <i className="fas fa-search pp-search-icon" />
          <input
            className="pp-search"
            placeholder={t('PeoplePage.searchPlaceholder')}
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(0); }}
          />
        </div>
        <select
          className="pp-filter"
          value={typeFilter}
          onChange={e => { setTypeFilter(e.target.value); setPage(0); }}
        >
          <option value="">{t('PeoplePage.allTypes')}</option>
          <option value="HUMAN">{t('PeoplePage.typeHuman')}</option>
          <option value="SERVICE">{t('PeoplePage.serviceAccount')}</option>
          <option value="ROLE">{t('PeoplePage.typeRole')}</option>
        </select>
        <select
          className="pp-filter"
          value={sourceFilter}
          onChange={e => { setSourceFilter(e.target.value); setPage(0); }}
        >
          <option value="">{t('PeoplePage.allSources')}</option>
          {Object.entries(SOURCE_META).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
        <select
          className="pp-filter"
          value={deptFilter}
          onChange={e => { setDeptFilter(e.target.value); setPage(0); }}
        >
          <option value="">{t('PeoplePage.allDepartments')}</option>
          {departments.map(d => (
            <option key={d} value={d}>{d}</option>
          ))}
        </select>
      </div>

      {/* Table */}
      {people.length === 0 ? (
        <div className="pp-empty">
          <i className="fas fa-user-slash" />
          <p>{t('PeoplePage.emptyState')}</p>
        </div>
      ) : (
        <div className="pp-table-wrap">
          <table className="pp-table">
            <thead>
              <tr>
                <th>{t('PeoplePage.colName')}</th>
                <th>{t('PeoplePage.colType')}</th>
                <th>{t('PeoplePage.colEmail')}</th>
                <th>{t('PeoplePage.colRoleTitle')}</th>
                <th>{t('PeoplePage.colDepartment')}</th>
                <th>{t('PeoplePage.colSource')}</th>
                <th>{t('PeoplePage.colStatus')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {people.map(person => (
                <tr key={person.id} className={!person.isActive ? 'pp-row-inactive' : ''}>
                  <td>
                    <div className="pp-person-cell">
                      <div
                        className="pp-avatar"
                        style={{ background: avatarColor(person.displayName) }}
                      >
                        {getInitials(person.displayName)}
                      </div>
                      <span className="pp-name">{person.displayName || '—'}</span>
                      {person.linkedCount > 1 && (
                        <span
                          className="pp-linked-badge"
                          title={t('PeoplePage.linkedTip', { defaultValue: 'Accounts linked into one person — click to view' })}
                          onClick={() => setExpanded(e => ({ ...e, [person.personId]: !e[person.personId] }))}
                        >
                          <i className="fas fa-link" /> {t('PeoplePage.linkedCount', { count: person.linkedCount, defaultValue: '{{count}} linked accounts' })}
                        </span>
                      )}
                      {!person.linkedCount && person.suggestedGroupId && (
                        <span className="pp-dup-badge" title={t('PeoplePage.dupTip', { defaultValue: 'A possible duplicate was found — see the panel above to confirm' })}>
                          <i className="fas fa-exclamation-triangle" /> {t('PeoplePage.dupBadge', { defaultValue: 'possible duplicate' })}
                        </span>
                      )}
                    </div>
                    {person.linkedCount > 1 && expanded[person.personId] && (
                      <div className="pp-linked-detail">
                        {(person.linkedAccounts || []).map(a => (
                          <div className="pp-linked-acct" key={a.id}>
                            {sourceChip(a.source)}
                            <span>{a.displayName || '—'}</span>
                            {a.email && <span className="pp-email">· {a.email}</span>}
                          </div>
                        ))}
                        <div>
                          <button
                            className="pp-unmerge-btn"
                            onClick={() => unmergePerson(person.personId)}
                            title={t('PeoplePage.unmergeTip', { defaultValue: 'Unlink these accounts' })}
                          >
                            <i className="fas fa-unlink" /> {t('PeoplePage.unmerge', { defaultValue: 'Unmerge' })}
                          </button>
                        </div>
                      </div>
                    )}
                  </td>
                  <td>{typeBadge(person.accountType)}</td>
                  <td className="pp-email">{person.email || '—'}</td>
                  <td className="pp-role">{person.externalRole || '—'}</td>
                  <td className="pp-dept">{person.department || '—'}</td>
                  <td>{sourceChip(person.source)}</td>
                  <td>{statusBadge(person.isActive)}</td>
                  <td>
                    {person.isActive && (
                      <button
                        className="pp-offboard-btn"
                        title={t('PeoplePage.offboardEmployee')}
                        onClick={() => setOffboarding(person)}
                      >
                        <i className="fas fa-user-slash" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {offboarding && (
        <OffboardingModal
          person={offboarding}
          onClose={() => setOffboarding(null)}
          onDone={(id) => {
            setPeople(prev => prev.map(p =>
              p.id === id ? { ...p, isActive: false, status: 'OFFBOARDED' } : p
            ));
            setOffboarding(null);
          }}
        />
      )}

      {totalPages > 1 && (
        <div className="pp-pagination">
          <button
            className="pp-page-btn"
            disabled={page === 0}
            onClick={() => setPage(p => p - 1)}
          >
            <i className="fas fa-chevron-left" />
          </button>
          <span className="pp-page-info">{t('PeoplePage.pageInfo', { current: page + 1, total: totalPages })}</span>
          <button
            className="pp-page-btn"
            disabled={page >= totalPages - 1}
            onClick={() => setPage(p => p + 1)}
          >
            <i className="fas fa-chevron-right" />
          </button>
        </div>
      )}
    </div>
  );
}

export default PeoplePage;
